import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/axiosInstance";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import { FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { buildCategoryOptions, buildSubcategoryOptions, normaliseName } from "../utils/categoryOptions";

// Dropdown value that switches a field to typing a new name.
const NEW_OPTION = "__new__";

function AddCategory() {
  const { t } = useTranslation();
  const [categoryId] = useSearchParams();
  const categoryIdParam = categoryId.get("id");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isFormProcessing, setIsFormProcessing] = useState(false);

  // New state variables for missing fields
  const [columnHeader, setColumnHeader] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [isTrending, setIsTrending] = useState(false);
  const [categoryCount, setCategoryCount] = useState({
    total: 0,
    remaining: 10,
    maxCategories: 10
  });
  const [showCategoryLimit, setShowCategoryLimit] = useState(false);
  const [charLimitWarning, setCharLimitWarning] = useState(false);

  // Marketplace (Super Admin) and this store's category rows, for the
  // Category / Subcategory dropdowns. "select" picks from them, "new" types
  // a new name.
  const [categoryRows, setCategoryRows] = useState([]);
  const [categoryMode, setCategoryMode] = useState("select");
  const [subcategoryMode, setSubcategoryMode] = useState("select");

  const categoryOptions = useMemo(() => buildCategoryOptions(categoryRows), [categoryRows]);
  const subcategoryOptions = useMemo(
    () => buildSubcategoryOptions(
      categoryRows.filter((row) => normaliseName(row.category) === normaliseName(category)),
      category
    ),
    [categoryRows, category]
  );
  // Editing changes one of the store's own rows, so it keeps the text boxes.
  const hasCategoryOptions = categoryOptions.marketplace.length + categoryOptions.store.length > 0;
  const showCategorySelect = !categoryIdParam && hasCategoryOptions && categoryMode === "select";
  const showSubcategorySelect = showCategorySelect && category !== "" &&
    subcategoryOptions.length > 0 && subcategoryMode === "select";
  // The store already has this category + subcategory, so there is nothing
  // to save - it can be picked in the product form as it is.
  const existingStoreSubcategory = showSubcategorySelect && subcategoryOptions.some(
    (option) => option.inStore && option.subcategory === subcategory
  );


  const navigate = useNavigate();

  useEffect(() => {
    const businessId = localStorage.getItem('business_id');
    if (businessId) {
      api.get(`/category/count/${businessId}`)
        .then(res => {
          if (res.data.status === "success") {
            setCategoryCount({
              total: res.data.result.total_categories,
              remaining: res.data.result.remaining_categories,
              maxCategories: res.data.result.max_categories
            });
            setShowCategoryLimit(true);
          }
        })
        .catch(err => {
          console.error("Error fetching category count:", err);
          setShowCategoryLimit(false);
        });
    }
  }, []);
  useEffect(() => {
    const businessId = localStorage.getItem('business_id');
    if (categoryIdParam || !businessId) return;
    api.get('/category/list', { params: { business_id: businessId, include_platform: 'true', limit: 100000 } })
      .then((res) => {
        if (res.data.status === "success" && Array.isArray(res.data.result)) {
          setCategoryRows(res.data.result);
        }
      })
      .catch((err) => console.error("Error fetching categories:", err));
  }, [categoryIdParam]);

  const handleCategoryChoice = (value) => {
    setSubcategory("");
    setSubcategoryMode("select");
    if (value === NEW_OPTION) {
      setCategoryMode("new");
      setCategory("");
      return;
    }
    // Keep a default "<category> Collection" header in step with the choice.
    if (!columnHeader || columnHeader === `${category} Collection`) {
      setColumnHeader(value ? `${value} Collection` : "");
    }
    setCategory(value);
  };

  const handleSubcategoryChoice = (value) => {
    if (value === NEW_OPTION) {
      setSubcategoryMode("new");
      setSubcategory("");
      return;
    }
    setSubcategory(value);
  };

  const backToCategoryList = () => {
    setCategoryMode("select");
    setSubcategoryMode("select");
    setCategory("");
    setSubcategory("");
    setCharLimitWarning(false);
  };

  useEffect(() => {
    if (categoryIdParam) {
      // Fetch category details when editing
      const fetchCategoryDetails = async () => {
        try {
          const response = await api.get('/category/list', { params: { category_id: categoryIdParam } });
          if (response.data.status === "success") {
            const categoryData = response.data.result;

            // Populate form fields with fetched data
            setCategory(categoryData.category || "");
            setSubcategory(categoryData.subcategory || "");
            setColumnHeader(categoryData.column_header || "");
            setIsFeatured(categoryData.is_featured || false);
            setIsTrending(categoryData.is_trending || false);
          } else {
            setErrorMessage("Failed to fetch category details");
          }
        } catch (error) {
          console.error("Error fetching category details:", error);
          setErrorMessage("Error fetching category details");
        }
      };

      fetchCategoryDetails();
    }
  }, [categoryIdParam]);

  const handleAddCategory = async (e) => {
    e.preventDefault();

    setIsFormProcessing(true);
    let valid = true;

    // Validate form fields
    const businessId = localStorage.getItem('business_id');

    // A category saved without business_id would become a platform (Super
    // Admin) category, so never submit one from the store admin panel.
    if (!businessId) {
      setErrorMessage(t('categoryTab.missingBusiness'));
      valid = false;
    } else if (category.trim() === "") {
      setErrorMessage("Category is required");
      valid = false;
    } else if (subcategory.trim() === "") {
      setErrorMessage("Subcategory is required");
      valid = false;
    } else if (existingStoreSubcategory) {
      setErrorMessage(t('form.subcategoryExists', { category, subcategory }));
      valid = false;
    } else if (columnHeader.trim() === "") {
      setErrorMessage("Column Header is required");
      valid = false;
    } else {
      setErrorMessage("");
    }

    if (!valid) {
      setIsFormProcessing(false);
      return;
    }

    try {
      const apiUrl = categoryIdParam ? '/category/update' : '/category/create';
      const payload = {
        category: category,
        subcategory: subcategory,
        category_id: categoryIdParam ? categoryIdParam : "",
        type: 'gift',
        column_header: columnHeader,
        is_featured: isFeatured,
        is_trending: isTrending,
        business_id: businessId
      };

      api
        .post(apiUrl, payload)
        .then((res) => {
          if (res.data.status === "success") {
            setSuccessMessage(
              categoryIdParam
                ? "Category updated successfully!"
                : "Category added successfully!"
            );

            // Refresh category count after successful creation
            if (!categoryIdParam) {
              const businessId = localStorage.getItem('business_id');
              if (businessId) {
                api.get(`/category/count/${businessId}`)
                  .then(countRes => {
                    if (countRes.data.status === "success") {
                      setCategoryCount({
                        total: countRes.data.result.total_categories,
                        remaining: countRes.data.result.remaining_categories,
                        maxCategories: countRes.data.result.max_categories
                      });
                    }
                  })
                  .catch(err => console.error("Error refreshing category count:", err));
              }
            }

            setTimeout(() => {
              setSuccessMessage("");
              navigate("/category");
            }, 2000);
          } else {
            setErrorMessage("Failed to add/update category");
          }
        })
        .catch((err) => {
          console.error("Category creation/update error:", err);

          // Handle specific error for category limit
          if (err.response?.data?.error?.field_name === "category_limit_exceeded") {
            setErrorMessage(err.response.data.error.message);
          }
          // Handle duplicate subcategory error
          else if (err.response && err.response.data && err.response.data.error && err.response.data.error.code === 701) {
            setErrorMessage(err.response.data.error.message);
          }
          else if (err.response && err.response.data && err.response.data.message) {
            setErrorMessage(err.response.data.message);
          }
        })
        .finally(() => setIsFormProcessing(false));
    } catch (err) {
      console.error("Error:", err);
      setErrorMessage("Error while processing the form");
      setIsFormProcessing(false);
    }
  };

  return (
    <div className="wrapper">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6 d-flex align-items-center">
                <FaArrowLeft
                  style={{ cursor: "pointer", marginRight: "10px" }}
                  onClick={() => navigate("/category")}
                  size={20}
                />

                <h1 className="m-0 txt_lft">
                  {categoryIdParam ? t('categoryTab.editCategory') : t('categoryTab.addCategory')}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {showCategoryLimit && !categoryIdParam && (
          <div className="container-fluid mb-3">
            <div className="row">
              <div className="col-12">
                <div className={`alert ${categoryCount.remaining > 0 ? 'alert-info' : 'alert-warning'}`}>
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>{t('categoryTab.categoryLimit')} </strong>
                  {t('categoryTab.categoryLimitInfo')} <strong>{categoryCount.total}</strong> {t('categoryTab.categoryLimitInfo1')} <strong>{categoryCount.maxCategories}</strong> {t('categoryTab.categoryLimitInfo2')}
                  {categoryCount.remaining > 0 ? (
                    <span> {t('categoryTab.categoryLimitRemaining')} <strong>{categoryCount.remaining}</strong> {t('categoryTab.categoryLimitRemaining1')}</span>
                  ) : (
                    <span> {t('categoryTab.categoryLimitReached')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <form
                      className="form-horizontal"
                      onSubmit={handleAddCategory}
                    >
                      <div className="card-body">
                        {/* Category Field */}

                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            {t('categoryTab.title')}<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            {showCategorySelect ? (
                              <>
                                <select
                                  className="form-control"
                                  value={category}
                                  onChange={(e) => handleCategoryChoice(e.target.value)}
                                  aria-label={t('categoryTab.title')}
                                >
                                  <option value="">{t('form.chooseCategory')}</option>
                                  {categoryOptions.store.length > 0 && (
                                    <optgroup label={t('form.myStoreCategories')}>
                                      {categoryOptions.store.map((name) => (
                                        <option key={`s-${name}`} value={name}>{name}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {categoryOptions.marketplace.length > 0 && (
                                    <optgroup label={t('form.marketplaceCategories')}>
                                      {categoryOptions.marketplace.map((name) => (
                                        <option key={`m-${name}`} value={name}>{name}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  <option value={NEW_OPTION}>{t('form.addNewCategory')}</option>
                                </select>
                                {categoryOptions.marketplace.length > 0 && (
                                  <small className="form-text text-muted">{t('form.marketplaceCategoryHint')}</small>
                                )}
                              </>
                            ) : (
                            <>
                            <input
                              className="form-control"
                              type="text"
                              value={category}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 12) {
                                  setCategory(value);
                                  setCharLimitWarning(false);
                                } else {
                                  setCharLimitWarning(true);
                                  setCategory(value.slice(0, 12));
                                }
                              }}
                              placeholder={t('form.enterCategory')}
                            />
                            {!categoryIdParam && hasCategoryOptions && (
                              <button type="button" className="btn btn-link btn-sm p-0" onClick={backToCategoryList}>
                                {t('form.chooseFromList')}
                              </button>
                            )}
                            </>
                            )}

                            {/* Character limit warning */}
                            {charLimitWarning && (
                              <small className="form-text text-warning">
                                <i className="fas fa-exclamation-triangle"></i> Only 12 characters are allowed.
                              </small>
                            )}

                            {/* Existing category count warning */}
                            {!charLimitWarning && showCategoryLimit && !categoryIdParam && (
                              <small className="form-text text-muted">
                                {categoryCount.remaining > 0 ? (
                                  <span className="text-info">
                                    <i className="fas fa-lightbulb mr-1"></i>
                                    {categoryCount.remaining} {t('categoryTab.categoryLimitHint')}
                                  </span>
                                ) : (
                                  <span className="text-warning">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    {t('categoryTab.categoryLimitWarning')}
                                  </span>
                                )}
                              </small>
                            )}
                          </div>
                        </div>



                        {/* Subcategory Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            {t('form.subcategory')}<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            {showSubcategorySelect ? (
                              <>
                              <select
                                className="form-control"
                                value={subcategory}
                                onChange={(e) => handleSubcategoryChoice(e.target.value)}
                                aria-label={t('form.subcategory')}
                              >
                                <option value="">{t('form.chooseSubcategory')}</option>
                                {subcategoryOptions.map((option) => (
                                  <option key={option.id} value={option.subcategory}>
                                    {option.subcategory}{option.inStore ? ` ${t('form.alreadyAdded')}` : ""}
                                  </option>
                                ))}
                                <option value={NEW_OPTION}>{t('form.addNewSubcategory')}</option>
                              </select>
                              {existingStoreSubcategory && (
                                <small className="form-text text-info">
                                  <i className="fas fa-info-circle"></i> {t('form.subcategoryExists', { category, subcategory })}
                                </small>
                              )}
                              </>
                            ) : (
                              <>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={subcategory}
                                  onChange={(e) => setSubcategory(e.target.value)}
                                  placeholder={t('form.enterSubcategory')}
                                />
                                {showCategorySelect && category !== "" && subcategoryOptions.length > 0 && (
                                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => { setSubcategoryMode("select"); setSubcategory(""); }}>
                                    {t('form.chooseFromList')}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Type Field */}
                        {/* <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Type<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <select
                              className="form-control"
                              value={type}
                              onChange={(e) => setType(e.target.value)}
                            >
                              <option value="">Select type</option>
                              <option value="greeting_card">Greeting card</option>
                              <option value="gift">Product</option>
                            </select>
                          </div>
                        </div> */}

                        {/* Column Header Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            {t('form.columnHeader')}<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              className="form-control"
                              type="text"
                              value={columnHeader}
                              onChange={(e) => setColumnHeader(e.target.value)}
                              placeholder={t('form.enterColumnHeader')}
                            />
                          </div>
                        </div>

                        {/* Featured and Trending Checkboxes */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            {t('form.settings')}
                          </label>
                          <div className="col-12 col-md-8">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="isFeatured"
                                checked={isFeatured}
                                onChange={(e) => {
                                  setIsFeatured(e.target.checked);
                                  if (e.target.checked) setIsTrending(false);
                                }}
                              />
                              <label className="form-check-label" htmlFor="isFeatured">
                                {t('form.featuredCategory')}
                              </label>
                            </div>
                            <div className="form-check mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="isTrending"
                                checked={isTrending}
                                onChange={(e) => {
                                  setIsTrending(e.target.checked);
                                  if (e.target.checked) setIsFeatured(false);
                                }}
                              />
                              <label className="form-check-label" htmlFor="isTrending">
                                {t('form.trendingCategory')}
                              </label>
                            </div>
                            <small className="form-text text-muted">
                              {t('form.featuredTrendingHint')}
                            </small>
                          </div>
                        </div>

                        {/* Error Message */}
                        {errorMessage && (
                          <div className="text-danger">{errorMessage}</div>
                        )}

                        {/* Success Message */}
                        {successMessage && (
                          <div className="text-success">{successMessage}</div>
                        )}
                      </div>

                      {/* Form Submit Button */}
                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isFormProcessing}
                        >
                          {categoryIdParam ? "Update" : "Add"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AddCategory;